import { Box, FormHelperText, Grid, SxProps, Theme, Typography, Divider } from '@mui/material';
import { green } from '@mui/material/colors';
import { useCallback, useEffect, useRef } from 'react';
import { useFormContext } from 'react-hook-form';
import { useParams } from 'react-router-dom';
import { InventoryKicker } from 'quote-detail/inventory-kicker';
import { MarginButton } from 'quote-detail/margin-button';
import { NumberText } from 'quote-detail/number-text';
import { OutOfScopeCustomerVendor } from 'quote-detail/out-of-scope-customer-vendor';
import { RecalculateProduct } from 'quote-detail/recalculate-product';
import { RevisionReasonField } from 'quote-detail/revision-reason-field';
import { AppBadge } from 'shared/components/app-badge';
import { NumberTextField } from 'shared/controls';
import { Brands, Product, ProductNumberValues } from 'shared/types';
import {
  buildCostHelperText,
  buildListPriceHelperText,
  buildFieldNameWithIndex,
  costDiscountToPrice,
  costMarginToPrice,
  round,
  costPriceToMargin,
  costPriceToDiscount,
  initialDateCompare,
  costMarginToPriceAdjustedByFreightAdder,
  formatDate,
} from 'shared/utility';
import { discountRecommendationBrands, lineItemNoteBrands, listPriceBrands, PERMISSIONS } from 'shared/constants';
import { ProductCardModelInputsButton } from './product-card-model-inputs-button';
import { useUserPermissions } from 'shared/hooks/use-user-permissions';
import { LineItemNotes } from './line-item-notes';

const rootStyles: SxProps<Theme> = {
  width: '100%',
  position: 'relative',
};
const marginHeaderContainerStyles: SxProps<Theme> = (theme: Theme) => ({
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'flex-start',
  marginLeft: theme.spacing(3),
});
const marginHeaderStyles: SxProps<Theme> = {
  textTransform: 'uppercase',
  fontWeight: 'normal',
  opacity: 0.5,
};
const revisedMarginStyles: SxProps<Theme> = {
  '& .MuiInputBase-root': {
    backgroundColor: green[50],
  },
};
const dividerStyles: SxProps<Theme> = (theme: Theme) => ({
  marginLeft: theme.spacing(4),
  marginRight: theme.spacing(4),
});

const gridContainerStyles: SxProps<Theme> = (theme: Theme) => ({
  flexDirection: 'row',
  [theme.breakpoints.down('lg')]: {
    flexDirection: 'column-reverse',
  },
});

const modelInputContainerStyles: SxProps<Theme> = (theme: Theme) => ({
  marginLeft: theme.spacing(1.25),
  [theme.breakpoints.down('lg')]: {
    marginLeft: theme.spacing(0),
    marginBottom: theme.spacing(3.75),
  },
});

interface ProductCardProps {
  product: Product;
  editMode: boolean;
  index: number;
}
export function ProductCard({ product, editMode, index }: ProductCardProps) {
  const {
    setValue,
    setError,
    formState: { errors },
  } = useFormContext();
  const { brand } = useParams<{ brand: Brands }>();
  const permissions = useUserPermissions();
  const isI2PUser: boolean = permissions.includes(PERMISSIONS.i2puser);
  const isDiscountBasedBrand: boolean = discountRecommendationBrands.includes(brand);
  const isListPriceBrand: boolean = listPriceBrands.includes(brand);
  const isCBEbrand: boolean = brand === 'cbe';
  const isNianticBrand: boolean = brand === 'nianticseal';
  const islineItemNoteBrand: boolean = lineItemNoteBrands.includes(brand);
  const lastSavedCost = useRef(isDiscountBasedBrand ? product.listPrice : product.lastCostPaid);
  const buildFieldName = buildFieldNameWithIndex<Product>(index);
  const listPriceEffectiveDateBadge =
    product.listPriceEffectiveDate && product.listPriceEffectiveDate !== formatDate(new Date(initialDateCompare))
      ? product.listPriceEffectiveDate
      : '';

  useEffect(() => {
    if (lastSavedCost && Number(lastSavedCost.current) !== (isDiscountBasedBrand ? Number(product.listPrice) : Number(product.lastCostPaid))) {
      setError('products', {
        message: 'There are products that need to be recalculated',
        type: 'custom',
      });
    }
  }, [product.listPrice, product.lastCostPaid, setError, isDiscountBasedBrand]);

  const setRevisedCalculationEntity = useCallback(
    (entity: number) => {
      setValue(buildFieldName(isDiscountBasedBrand ? 'revisedDiscount' : 'revisedMargin'), entity);

      if (product.editRevisedPrice) {
        const costAdjustedByExchangeRate = Number(product.lastCostPaid) * Number(product.exchangerate);
        const newPrice = isDiscountBasedBrand
          ? costDiscountToPrice(Number(product.listPrice), entity)
          : isCBEbrand
            ? costMarginToPriceAdjustedByFreightAdder(costAdjustedByExchangeRate, entity, Number(product.freightadder))
            : costMarginToPrice(Number(product.lastCostPaid), entity, isNianticBrand ? 4 : 2);
        setValue(buildFieldName('revisedPrice'), newPrice);
      }
    },
    [product, isDiscountBasedBrand, setValue, buildFieldName, isCBEbrand, isNianticBrand]
  );

  const entityChange = (entity: number) => {
    const costAdjustedByExchangeRate = Number(product.lastCostPaid) * Number(product.exchangerate);
    const price = isDiscountBasedBrand
      ? costDiscountToPrice(Number(product.listPrice), entity)
      : isCBEbrand
        ? costMarginToPriceAdjustedByFreightAdder(costAdjustedByExchangeRate, entity, Number(product.freightadder))
        : costMarginToPrice(Number(product.lastCostPaid), entity, isNianticBrand ? 4 : 2);
    const extendedPrice = price * Number(product.quantity);

    setValue(buildFieldName('revisedPrice'), price);
    setValue(buildFieldName('extendedPrice'), extendedPrice);
  };

  const priceChange = (price: number) => {
    const margin = costPriceToMargin(Number(product.lastCostPaid), price);
    const discount = costPriceToDiscount(Number(product.listPrice), price);
    const extendedPrice = price * Number(product.quantity);

    if (isDiscountBasedBrand) {
      setValue(buildFieldName('revisedDiscount'), discount);
    } else {
      setValue(buildFieldName('revisedMargin'), margin);
    }
    setValue(buildFieldName('extendedPrice'), extendedPrice);
  };

  const quantityChange = (quantity: number) => {
    const extendedPrice = product.revisedPrice * quantity;

    setValue(buildFieldName('extendedPrice'), extendedPrice);
  };

  const freightAdderChange = (value: number) => {
    setValue(buildFieldName('freightadder'), value);
    setValue(buildFieldName('recalculate'), true);
  };

  const useTargetEntity = useCallback(() => {
    setRevisedCalculationEntity(isDiscountBasedBrand ? product.targetDiscount : product.targetMargin);
    setValue(buildFieldName('revisedReason'), '');
  }, [setRevisedCalculationEntity, isDiscountBasedBrand, setValue, buildFieldName, product]);

  const chooseEntity = useCallback(
    (entity: keyof ProductNumberValues) => () => {
      setRevisedCalculationEntity(product[entity]);
    },
    [setRevisedCalculationEntity, product]
  );

  const useCompetitive = useCallback(
    () => chooseEntity(isDiscountBasedBrand ? 'competitiveDiscount' : 'competitiveMargin')(),
    [isDiscountBasedBrand, chooseEntity]
  );
  const useStretch = useCallback(() => chooseEntity(isDiscountBasedBrand ? 'stretchDiscount' : 'maxMargin')(), [
    isDiscountBasedBrand,
    chooseEntity,
  ]);

  const formatPercent = (num: number, places: number = 2) => {
    return (num * 100).toFixed(places);
  };

  const editRevisedPrice = () => {
    setValue(buildFieldName('editRevisedPrice'), true);
  };
  const editRevisedMargin = () => {
    setValue(buildFieldName('editRevisedPrice'), false);
  };

  if (product.isCustomerVendorContracted || product.outOfScope) {
    return <OutOfScopeCustomerVendor />;
  }

  const revisedMarginIsLessThanTarget = round(product.revisedMargin, 4) < round(product.targetMargin, 4);
  const revisedPriceCondition = false;

  const revisionReasonRequired = product.editRevisedPrice ? revisedPriceCondition : revisedMarginIsLessThanTarget;

  const costHelperText = buildCostHelperText(product.costEffectiveDate, brand);
  const listPriceHelperText = buildListPriceHelperText(product.listPriceEffectiveDate);

  return (
    <Box sx={rootStyles}>
      {editMode && product.recalculate && <RecalculateProduct index={index} product={product} />}
      <Grid display='flex' sx={gridContainerStyles}>
        <Grid container direction='column' spacing={1}>
          <Grid item xs container spacing={4} alignItems='flex-start'>
            <Grid item xs={12} lg sx={revisedMarginStyles}>
              {product.editRevisedPrice ? (
                <div style={{ marginTop: '-10px' }}>
                  <Typography variant='subtitle1'>Revised {isDiscountBasedBrand ? 'Discount' : 'Margin'}</Typography>
                  <NumberText
                    suffix='%'
                    variant='subtitle1'
                    text={formatPercent(isDiscountBasedBrand ? product.revisedDiscount : product.revisedMargin)}
                    onClick={editRevisedMargin}
                  />
                  {editMode && (
                    <FormHelperText>Click to edit {isDiscountBasedBrand ? 'discount' : 'margin'}</FormHelperText>
                  )}
                </div>
              ) : (
                <NumberTextField
                  label={`Revised ${isDiscountBasedBrand ? 'Discount' : 'Margin'}`}
                  name={buildFieldName(isDiscountBasedBrand ? 'revisedDiscount' : 'revisedMargin')}
                  defaultValue={product[isDiscountBasedBrand ? 'revisedDiscount' : 'revisedMargin']}
                  percent
                  {...(editMode ? { onChangeValue: entityChange } : { disabled: true })}
                />
              )}
            </Grid>
            <Grid item xs={12} lg>
              {editMode ? (
                <RevisionReasonField
                  label='Revision Reason'
                  name={buildFieldName('revisedReason')}
                  defaultValue={product.revisedReason}
                  rules={{
                    required: {
                      message: 'Revision Reason is required',
                      value: revisionReasonRequired,
                    },
                  }}
                  error={Boolean(errors?.products?.[index]?.revisedReason)}
                  helperText={errors?.products?.[index]?.revisedReason.message}
                />
              ) : (
                <RevisionReasonField
                  label='Revision Reason'
                  name={buildFieldName('revisedReason')}
                  defaultValue={product.revisedReason}
                  disabled
                />
              )}
            </Grid>
            <Grid item xs={12} lg>
              {editMode ? (
                <InventoryKicker
                  label='Item In Inventory'
                  name={buildFieldName('inventoryKicker')}
                  defaultValue={product.inventoryKicker}
                />
              ) : (
                <InventoryKicker
                  label='Item In Inventory'
                  name={buildFieldName('inventoryKicker')}
                  defaultValue={product.inventoryKicker}
                  disabled
                />
              )}
            </Grid>
            <Grid item xs={12} lg>
              {editMode ? (
                <AppBadge invisible={!product.quantityBreakFlag} badgeContent={'!'} fullWidth title={costHelperText}>
                  <NumberTextField
                    label='Revised Cost'
                    name={buildFieldName('lastCostPaid')}
                    defaultValue={Number(product.lastCostPaid)}
                    prefix='$'
                    decimalScale={isNianticBrand ? 4 : 2}
                    formatDecimalsOnBlur={isNianticBrand}
                    fixedDecimalScale={!isNianticBrand}
                    triggerChangeOnBlur
                    onChangeValue={newPrice => {
                      if (Number(lastSavedCost.current) !== newPrice) {
                        setValue(buildFieldName('recalculate'), true);
                        lastSavedCost.current = newPrice;
                      }
                    }}
                  />
                </AppBadge>
              ) : (
                <NumberTextField
                  label='Revised Cost'
                  decimalScale={isNianticBrand ? 4 : 2}
                  name={buildFieldName('lastCostPaid')}
                  defaultValue={Number(product.lastCostPaid)}
                  prefix='$'
                  fixedDecimalScale
                  disabled
                />
              )}
            </Grid>
            {(isDiscountBasedBrand || isListPriceBrand) && (
              <Grid item xs={12} lg>
                {editMode ? (
                  <AppBadge
                    invisible={!product.quantityBreakFlag}
                    badgeContent={'!'}
                    fullWidth
                    title={listPriceHelperText}
                  >
                    <NumberTextField
                      label='List Price'
                      name={buildFieldName('listPrice')}
                      defaultValue={Number(product.listPrice)}
                      prefix='$'
                      fixedDecimalScale
                      triggerChangeOnBlur
                      onChangeValue={newPrice => {
                        if (Number(lastSavedCost.current) !== newPrice) {
                          setValue(buildFieldName('recalculate'), true);
                          lastSavedCost.current = newPrice;
                        }
                      }}
                    />
                  </AppBadge>
                ) : (
                  <NumberTextField
                    label='List Price'
                    name={buildFieldName('listPrice')}
                    defaultValue={Number(product.listPrice)}
                    prefix='$'
                    fixedDecimalScale
                    disabled
                  />
                )}
              </Grid>
            )}
            {isCBEbrand && (
              <>
                {editMode ? (
                  <Grid item xs={12} lg>
                    <NumberTextField
                      label='Freight Adder'
                      name={buildFieldName('freightadder')}
                      onChangeValue={freightAdderChange}
                      defaultValue={product.freightadder}
                      decimalScale={4}
                      triggerChangeOnBlur
                    />
                  </Grid>
                ) : (
                  <Grid item xs={12} lg>
                    <NumberTextField
                      label='Freight Adder'
                      name={buildFieldName('freightadder')}
                      defaultValue={product.freightadder}
                      decimalScale={4}
                      disabled
                    />
                  </Grid>
                )}
              </>
            )}
            <Grid item xs={12} lg>
              {editMode ? (
                <NumberTextField
                  label='Quantity'
                  name={buildFieldName('quantity')}
                  onChangeValue={quantityChange}
                  defaultValue={Number(product.quantity)}
                />
              ) : (
                <NumberTextField
                  label='Quantity'
                  name={buildFieldName('quantity')}
                  defaultValue={Number(product.quantity)}
                  disabled
                />
              )}
            </Grid>
            <Grid item xs sx={revisedMarginStyles}>
              {product.editRevisedPrice ? (
                editMode ? (
                  <div>
                    <NumberTextField
                      label='Revised Price'
                      name={buildFieldName('revisedPrice')}
                      defaultValue={product.revisedPrice}
                      prefix='$'
                      onChangeValue={priceChange}
                      fullWidth={false}
                      decimalScale={isNianticBrand ? 4 : 2}
                      fixedDecimalScale
                    />
                  </div>
                ) : (
                  <>
                    <Typography variant='subtitle1'>Revised Price</Typography>
                    <NumberText
                      variant='subtitle1'
                      prefix='$'
                      text={product.revisedPrice}
                      decimalScale={isNianticBrand ? 4 : 2}
                      fixedDecimalScale
                    />
                  </>
                )
              ) : (
                <div style={{ marginTop: '-10px' }}>
                  <Typography variant='subtitle1'>Revised Price</Typography>
                  <NumberText
                    prefix='$'
                    text={product.revisedPrice}
                    variant='subtitle1'
                    onClick={editRevisedPrice}
                    decimalScale={isNianticBrand ? 4 : 2}
                    fixedDecimalScale
                  />
                  {editMode && <FormHelperText>Click to edit price</FormHelperText>}
                </div>
              )}
            </Grid>
          </Grid>
          <Grid item xs container spacing={4} alignItems='center' justifyContent='space-evenly'>
            <Grid item xs container>
              <Box sx={marginHeaderContainerStyles}>
                <Typography variant='subtitle1' sx={marginHeaderStyles}>
                  {isDiscountBasedBrand ? 'Discount' : 'Margin'}
                </Typography>
                <Typography variant='subtitle1' sx={marginHeaderStyles}>
                  adjustment
                </Typography>
              </Box>
              <Divider sx={dividerStyles} orientation='vertical' variant='middle' flexItem />
              <Grid item xs container>
                <MarginButton
                  disabled={!editMode}
                  margin={isDiscountBasedBrand ? product.competitiveDiscount : product.competitiveMargin}
                  label={`Competitive ${isDiscountBasedBrand ? 'Discount' : 'Margin'}`}
                  onClick={useCompetitive}
                  color='red'
                />
                <MarginButton
                  disabled={!editMode}
                  margin={isDiscountBasedBrand ? product.targetDiscount : product.targetMargin}
                  label={`Target ${isDiscountBasedBrand ? 'Discount' : 'Margin'}`}
                  onClick={useTargetEntity}
                  color='orange'
                />
                <MarginButton
                  disabled={!editMode}
                  margin={isDiscountBasedBrand ? product.stretchDiscount : product.maxMargin}
                  label={`Stretch ${isDiscountBasedBrand ? 'Discount' : 'Margin'}`}
                  onClick={useStretch}
                  color='green'
                />
                {isDiscountBasedBrand ? (
                  <>
                    {Boolean(product.currentCustomerItemDiscount) && (
                      <MarginButton
                        disabled={!editMode}
                        margin={product.currentCustomerItemDiscount}
                        label='Current Customer Discount'
                        onClick={chooseEntity('currentCustomerItemDiscount')}
                        color='blue'
                      />
                    )}
                    {Boolean(product.premiumHistoricalDiscount) && (
                      <MarginButton
                        disabled={!editMode}
                        margin={product.premiumHistoricalDiscount}
                        label='Historical Premium'
                        onClick={chooseEntity('premiumHistoricalDiscount')}
                        color='blue'
                      />
                    )}
                    {Boolean(product.lastDiscountPaid) && (
                      <MarginButton
                        disabled={!editMode}
                        margin={product.lastDiscountPaid}
                        label='Last Discount Paid'
                        onClick={chooseEntity('lastDiscountPaid')}
                        color='grey'
                        badge={product.lastDiscountPaidDate}
                      />
                    )}
                  </>
                ) : (
                  <>
                    {Boolean(product.salesRepAdjustedMargin) && (
                      <MarginButton
                        disabled={!editMode}
                        margin={product.salesRepAdjustedMargin}
                        label='Sales Rep Adjusted'
                        onClick={chooseEntity('salesRepAdjustedMargin')}
                        color='blue'
                      />
                    )}
                    {product.displayPremiumHistorical && (
                      <MarginButton
                        disabled={!editMode}
                        margin={product.marginPercentileEighty}
                        label='Historical Premium'
                        onClick={chooseEntity('marginPercentileEighty')}
                        color='blue'
                      />
                    )}
                    {Boolean(product.marginAtList) && (
                      <MarginButton
                        disabled={!editMode}
                        margin={product.marginAtList}
                        label='Margin At List'
                        onClick={chooseEntity('marginAtList')}
                        color='blue'
                        badge={listPriceEffectiveDateBadge}
                      />
                    )}
                    {Boolean(product.lastMarginPaid) && (
                      <MarginButton
                        disabled={!editMode}
                        margin={product.lastMarginPaid}
                        label='Last Margin Paid'
                        onClick={chooseEntity('lastMarginPaid')}
                        color='grey'
                        badge={product.lastMarginPaidDate}
                      />
                    )}
                  </>
                )}
              </Grid>
            </Grid>
            <Grid item xs={2}>
              <NumberTextField
                label='Target Price'
                name={buildFieldName('recommendedPrice')}
                defaultValue={product.recommendedPrice}
                prefix='$'
                fullWidth={false}
                fixedDecimalScale
                decimalScale={isNianticBrand ? 4 : 2}
                disabled
              />
            </Grid>
          </Grid>

          {islineItemNoteBrand && <LineItemNotes product={product} editMode={editMode} index={index} />}
        </Grid>
        {!editMode && isI2PUser && (
          <Grid item sx={modelInputContainerStyles}>
            <ProductCardModelInputsButton index={index} />
          </Grid>
        )}
      </Grid>
    </Box>
  );
}
